import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import { ContextGraphData, RawContextNode } from '@/types/context/context-page';
import {
  createEdgesFromNodes,
  transformNodes,
} from '@/utils/context/context-graph';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';
import { 
  Box, 
  Card, 
  CardContent, 
  Collapse, 
  Divider, 
  Grid, 
  IconButton, 
  Stack, 
  Typography 
} from '@mui/material';
import { useEffect, useState } from 'react';
import NetworkGraph from '../visualizer/NetworkGraph';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';

interface TraceVizProps {
  traceId: string;
}

// Helper function to normalize MongoDB-style IDs in trace data
const normalizeTraceData = (contexts: any[]): RawContextNode[] => {
  return contexts.map(context => {
    // Convert _id object to string
    const normalizedId = context._id?.$oid || context._id;
    
    // Convert children_context_nodes from objects to strings
    const normalizedChildren = (context.children_context_nodes || []).map(
      (child: any) => child.$oid || child
    );
    
    // Convert parent_context_nodes from objects to strings
    const normalizedParents = (context.parent_context_nodes || []).map(
      (parent: any) => parent.$oid || parent
    );
    
    // Convert organization_id if it exists
    const normalizedOrgId = context.organization_id?.$oid || context.organization_id;
    
    // Convert user_id if it exists
    const normalizedUserId = context.user_id?.$oid || context.user_id;
    
    return {
      ...context,
      _id: normalizedId,
      children_context_nodes: normalizedChildren,
      parent_context_nodes: normalizedParents,
      organization_id: normalizedOrgId,
      user_id: normalizedUserId,
    };
  });
};

const fetchTraces = async (traceId: string) => {
  const fetchTracesResponse = await fetchWithRewrites(
    `/api/v1/context/traces/${traceId}`
  );

  if (!fetchTracesResponse.ok) {
    throw new Error('Could not fetch traces.');
    console.log('fetchTracesResponse', fetchTracesResponse);
  }

  const fetchTracesResponseJson: { trace: { contexts: any[] } } =
    await fetchTracesResponse.json();
  console.log('fetchTracesResponseJson', fetchTracesResponseJson.trace.contexts.length);
  
  // Normalize the MongoDB-style IDs before returning
  return normalizeTraceData(fetchTracesResponseJson.trace.contexts);
};

function ShowTrace({ traceId }: TraceVizProps) {
  const [traces, setTraces] = useState<RawContextNode[]>([]);
  const [tracesGraph, setTracesGraph] = useState<ContextGraphData>({
    edges: [],
    nodes: [],
  });
  const [expandedCards, setExpandedCards] = useState<Record<string, boolean>>({});

  const toggleCardExpansion = (cardId: string) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  const { setAlchemystStoreState, snackbarEntries } = useAlchemystStoreForAi(
    (store) => ({
      setAlchemystStoreState: store.setStoreState,
      snackbarEntries: store.snackbarEntries,
    })
  );
  useEffect(() => {
    fetchTraces(traceId)
      .then((tracesData) => {
        // Transform the data from simple traces to type ContextGraph here.
        const transformedNodes = transformNodes(tracesData);
        const edgesFromNodes = createEdgesFromNodes(transformedNodes);
        setTraces(tracesData);
        setTracesGraph({ nodes: transformedNodes, edges: edgesFromNodes });
        console.log('transformedNodes', transformedNodes.length);
        console.log('edgesFromNodes', edgesFromNodes.length);
      })
      .catch((e) => {
        console.log('error', e);
        setAlchemystStoreState({
          snackbarEntries: [
            ...snackbarEntries,
            {
              message: "Couldn't visualize the traces. Sorry :(",
              severity: 'error',
              type: 'alert',
              notificationId: crypto.randomUUID(),
            },
          ],
        });
      });
  }, [traceId]);
  
  // Format file size in a readable way
  const formatFileSize = (bytes?: number): string => {
    if (!bytes) return 'Unknown size';
    
    if (bytes < 1024) return bytes + ' B';
    else if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    else return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  };
  
  // Truncate text to a specific number of words
  const truncateWords = (text: string, wordCount: number = 5): string => {
    if (!text) return 'No content';
    const words = text.split(' ');
    if (words.length <= wordCount) return text;
    return words.slice(0, wordCount).join(' ') + '...';
  };
  
  return (
    <Box sx={{ height: '100%', width: '100%', padding: 4, overflow: 'auto' }}>
      <NetworkGraph data={tracesGraph} />
      
      <Box sx={{ mt: 4 }}>
        <Typography variant="h5" gutterBottom>
          Documents in this Trace
        </Typography>
        <Divider sx={{ mb: 3 }} />
        
        <Stack spacing={2}>
          {traces.map((trace, index) => {
            const cardId = trace._id || `trace-${index}`;
            const isExpanded = expandedCards[cardId] || false;
            
            return (
              <Card key={cardId} sx={{ width: '100%' }}>
                <CardContent sx={{ display: 'flex', justifyContent: 'space-between', pb: 1 }}>
                  <Box sx={{ width: '25%' }}>
                    <Typography variant="subtitle2" color="text.secondary">Document Name</Typography>
                    <Typography variant="body2">
                      {(trace.metadata as any)?.file_name || 'Unnamed Document'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ width: '25%' }}>
                    <Typography variant="subtitle2" color="text.secondary">Document Size</Typography>
                    <Typography variant="body2">
                      {formatFileSize((trace.metadata as any)?.size)}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ width: '25%' }}>
                    <Typography variant="subtitle2" color="text.secondary">Document Type</Typography>
                    <Typography variant="body2">
                      {(trace.metadata as any)?.doc_type || 'Unknown type'}
                    </Typography>
                  </Box>
                  
                  <Box sx={{ width: '25%', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                    <div>
                      <Typography variant="subtitle2" color="text.secondary">Segment Preview</Typography>
                      <Typography variant="body2" sx={{ 
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap'
                      }}>
                        {truncateWords(trace.content, 5) || 'No content'}
                      </Typography>
                    </div>
                    <IconButton 
                      size="small" 
                      onClick={() => toggleCardExpansion(cardId)}
                      sx={{ ml: 1, mt: -1 }}
                    >
                      {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>
                </CardContent>
                
                <Collapse in={isExpanded}>
                  <CardContent sx={{ pt: 0, pb: 2 }}>
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      Full Segment Content:
                    </Typography>
                    <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                      {trace.content || 'No content available'}
                    </Typography>
                  </CardContent>
                </Collapse>
              </Card>
            );
          })}
        </Stack>
      </Box>
    </Box>
  );
}

export default ShowTrace;
